import { useEffect } from 'react';
import type { StayAreaData, StayAreaPhoto } from '@shared/components/StayAreaTile/StayAreaTile.types';
import type { TravelDistancesBetweenPointsResponseFragment } from '../../../apps/admin/routes/WebsiteEditor/customComponents/Accommodations/Accommodations.graphql.generated';
import type { StayAreasQuery } from './stayAreas.graphql.generated';
import type { Maybe } from '@graphql/generated-base';
import { useStayAreasQuery } from './stayAreas.graphql.generated';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useStayAreaBestForTags } from './useStayAreaBestForTags';
import { addError } from '@shared/utils/logger';
import { addRendition } from '@shared/utils/photoRendition';
import { DEFAULT_STAY_AREAS_IMAGE } from '../constants';
import type { ApolloQueryResult } from '@apollo/client';

interface UseStayAreasReturn
  extends Readonly<{
    loading: boolean;
    error?: Error;
    stayAreas: ReadonlyArray<StayAreaData>;
    refetch: () => Promise<ApolloQueryResult<StayAreasQuery>>;
  }> {}

/**
 * Hook to get the stay areas data from GraphQL API.
 */
export const useStayAreas = (): UseStayAreasReturn => {
  const { eventInfo } = useEventInfo();
  const eventId = eventInfo?.eventId;

  const { data, loading, error, refetch } = useStayAreasQuery({
    variables: { eventId: eventId || '' },
    skip: !eventId,
    batchMode: 'fast',
  });

  const { parseBestForTag } = useStayAreaBestForTags();

  // Log error once when query fails
  useEffect(() => {
    if (error) {
      addError(error, {
        context: 'useStayAreas',
        message: 'Failed to fetch stay areas data',
        eventId,
      });
    }
  }, [error, eventId]);

  // Transform backend data to frontend format
  const stayAreasFromApi = data?.stayAreas ?? [];
  const stayAreas: ReadonlyArray<StayAreaData> = error
    ? []
    : stayAreasFromApi.map(area => {
        const carouselPhotos = parseStayAreaPhotos(area.name, area.photos, area.customPhotos);
        return {
          ...area,
          carouselPhotos,
          travelDistances: area.distances ? ({ commonDistance: area.distances } as TravelDistancesBetweenPointsResponseFragment) : undefined,
          features: parseStayAreaFeatures(area.whyThisAreaIsAGreatPick),
          bestForLabel: parseBestForTag(area.bestForTags ?? []),
        };
      });

  return {
    loading,
    error,
    stayAreas,
    refetch,
  };
};

const parseStayAreaFeatures = (whyThisAreaIsAGreatPick?: string | null): ReadonlyArray<string> => {
  return (
    whyThisAreaIsAGreatPick
      ?.split('\n')
      .map(line => line.replace(/^[•\-*→]\s*/, '').trim())
      .filter(Boolean) ?? []
  );
};

const parseStayAreaPhotos = (
  name: string,
  photos?: Maybe<ReadonlyArray<{ id: string; url: string }>>,
  customPhotos?: Maybe<ReadonlyArray<{ id: string; url: string; altTag?: string | null }>>,
): ReadonlyArray<StayAreaPhoto> => {
  const photosToUse: Array<{ id: string; url: string; altTag?: string | null }> = [...(customPhotos ?? []), ...(photos ?? [])];
  if (photosToUse.length === 0) {
    return [{ id: 'default', src: DEFAULT_STAY_AREAS_IMAGE, alt: name }];
  }
  return photosToUse.map(photo => ({ id: photo.id, src: addRendition({ url: photo.url, renditionSize: 'small' }), alt: photo.altTag ?? name }));
};
